import {
  PopoverMultiSelect,
  PopoverMultiSelectOptionData,
} from '@shared/components/popover-multiselect/PopoverMultiSelect';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { mayShowAEEvent } from '@shared/features/conversation-transcript/components/transcript/Transcript';
import { ActionExecutionEventType } from '@shared/generated/graphql';
import {
  useTaskVisSettings,
  VisSetting,
} from '@shared/hooks/useTaskVisSettings';
import { assertUnreachable } from 'clerk_common/enum';
import { LuLayers3 } from 'react-icons/lu';

type TaskVisualizationSettingsPopoverProps = {
  defaultVisSettings?: VisSetting;
};

export function TaskVisualizationSettingsPopover({
  defaultVisSettings,
}: TaskVisualizationSettingsPopoverProps = {}) {
  const { isVoomaAdmin } = useMeContext();
  const { visSettings: vis, setVisSettings } =
    useTaskVisSettings(defaultVisSettings);

  const handleOptionChange = async (id: string, newVal: boolean) => {
    const updated = newVal ? addToVis(vis, id) : removeFromVis(vis, id);
    await setVisSettings(updated);
  };

  const allEventTypes = getAllowedAEEventTypes(isVoomaAdmin);

  const handleSelectAll = async () => {
    await setVisSettings({
      ...vis,
      visibleActionExecutionEvents: [...allEventTypes],
    });
  };

  const handleDeselectAll = async () => {
    await setVisSettings({
      ...vis,
      visibleActionExecutionEvents: [],
    });
  };

  return (
    <PopoverMultiSelect
      options={toOptions(vis, isVoomaAdmin)}
      onOptionChange={handleOptionChange}
      onSelectAll={handleSelectAll}
      onDeselectAll={handleDeselectAll}
      buttonLabel=""
      buttonProps={{
        variant: 'secondary',
        isOutline: true,
        size: 'small',
        icon: <LuLayers3 size={14} />,
        tooltip: 'Visualization Settings',
      }}
    />
  );
}

function addToVis(visSettings: VisSetting, id: string) {
  if (isAeEventType(id)) {
    const aeEvents = visSettings.visibleActionExecutionEvents ?? [];
    return {
      ...visSettings,
      visibleActionExecutionEvents: aeEvents.includes(id)
        ? aeEvents
        : [...aeEvents, id],
    };
  }
  return visSettings;
}

function isAeEventType(id: string): id is ActionExecutionEventType {
  return Object.values(ActionExecutionEventType).includes(
    id as ActionExecutionEventType
  );
}

function removeFromVis(visSettings: VisSetting, id: string) {
  if (isAeEventType(id)) {
    const aeEvents = visSettings.visibleActionExecutionEvents ?? [];
    return {
      ...visSettings,
      visibleActionExecutionEvents: aeEvents.filter((ae) => ae !== id),
    };
  }
  return visSettings;
}

function getAllowedAEEventTypes(isVoomaAdmin: boolean) {
  return Object.values(ActionExecutionEventType).filter((aee) =>
    mayShowAEEvent(aee, isVoomaAdmin)
  );
}

function toOptions(
  visSettings: VisSetting,
  isVoomaAdmin: boolean
): PopoverMultiSelectOptionData[] {
  const { visibleActionExecutionEvents } = visSettings;
  const aeOptions = getAllowedAEEventTypes(isVoomaAdmin).map((ae) => ({
    id: ae,
    label: toLabel(ae),
    selected: visibleActionExecutionEvents?.includes(ae) ?? false,
  }));
  // TODO(mike): Add other options here.
  return [...aeOptions];
}

function toLabel(aee: ActionExecutionEventType) {
  switch (aee) {
    case ActionExecutionEventType.TOOL_USED:
      return 'Agent tool use';
    case ActionExecutionEventType.ESCALATION:
      return 'Escalations';
    case ActionExecutionEventType.DECISION_EDGE_EVALUATED:
      return 'Decision branch evaluations';
    case ActionExecutionEventType.SUPERVISOR_TOOL_USED:
      return 'Supervisor tool use';
    case ActionExecutionEventType.RAW_AGENT_MESSAGE:
      return 'Raw agent message';
    case ActionExecutionEventType.RAW_SUPERVISOR_MESSAGE:
      return 'Raw supervisor message';
    case ActionExecutionEventType.AGENT_REASONING:
      return 'Agent reasoning';
    case ActionExecutionEventType.SUPERVISOR_REASONING:
      return 'Supervisor reasoning';
    case ActionExecutionEventType.MEMORY_RECALLED:
      return 'Memory recollections';
    case ActionExecutionEventType.UNKNOWN:
      return 'Unknown events';
    case ActionExecutionEventType.SPAM_GUARD:
      return 'Spam guardrail';
    case ActionExecutionEventType.REMINDER:
      return 'Time elapsed';
    case ActionExecutionEventType.INSTRUCTION_MESSAGE:
      return 'Instructions received';
    case ActionExecutionEventType.STATUS_CHANGED:
      return 'Task status changes';
    case ActionExecutionEventType.AGENT_PROPOSED_OUTPUT:
      return 'Agent proposed output';
    case ActionExecutionEventType.SUPERVISOR_PROPOSED_OUTPUT:
      return 'Supervisor proposed output';
    case ActionExecutionEventType.WATCHER_CLOSED:
      return 'Watcher closed';
    case ActionExecutionEventType.WATCHER_POKED:
      return 'Watcher poked';
    default:
      return assertUnreachable(aee);
  }
}
